// Workflow usage and ratings (D73): stars, the workflows table's columns, a workflow's Ratings
// tab with its reviews and moderation, the rating weights setting, and rating a task.
import type { NotRateableReason } from "../../../ratings/model";
import { plural } from "../../helpers";

const p = plural("en");

export default {
  outOf5: (rating: string) => `${rating} out of 5`,
  noRatings: "No ratings yet",
  ratingCount: (n: number) => p(n, { one: "# rating", other: "# ratings" }),
  reviewCount: (n: number) => p(n, { one: "# review", other: "# reviews" }),
  usedIn: (n: number) => p(n, { one: "Used in # task", other: "Used in # tasks" }),
  columns: { rating: "Rating", usage: "Usage" },
  /** The five words for each score, 1 to 5. */
  scale: {
    results: ["Poor", "Fair", "Good", "Very good", "Excellent"],
    cost: ["Not worth it", "Poor value", "Fair value", "Good value", "Great value"],
  },
  summary: {
    description: "From the owners of finished tasks, over every version",
    overall: "Overall",
    results: "Results",
    cost: "Cost",
    usage: "Usage",
    average: "Average score",
    weighted: (results: string, cost: string) => `Weighted ${results} results, ${cost} cost`,
    executed: "Tasks that started a step",
    byVersion: "By version",
    byVersionDescription: "Usage and ratings of the tasks that ran each version",
    loadError: "Could not load the ratings",
  },
  reviews: {
    title: "Reviews",
    description: "Written reviews other users can read, newest first",
    none: "No reviews yet",
    noneBody: "A review shows here when a task's owner writes one with their rating.",
    yours: "Yours",
    more: "Show more",
    scores: (results: number, cost: number) => `Results ${results}/5 · Cost ${cost}/5`,
    version: (v: number) => `v${v}`,
    edited: (when: string) => `edited ${when}`,
    loadError: "Could not load the reviews",
  },
  moderation: {
    title: "Reviews and moderation",
    description: "Every rating, hidden or not. Hiding takes a review's text out of users' listings; its scores still count.",
    none: "No ratings yet",
    noneBody: "Ratings show here when task owners rate their finished tasks.",
    hidden: "Hidden",
    hide: "Hide",
    unhide: "Unhide",
    hideFor: (reviewer: string) => `Hide the review by ${reviewer}`,
    unhideFor: (reviewer: string) => `Unhide the review by ${reviewer}`,
    hiddenBy: (who: string, when: string) => `Hidden by ${who} ${when}`,
    noText: "No written review",
    rater: (id: string) => `rater ${id}`,
    task: "Task",
    hiddenToast: "Review hidden",
    shownToast: "Review shown again",
  },
  weights: {
    title: "Rating weights",
    description: "How much each score counts in a workflow's overall rating",
    explanation:
      "A workflow's overall rating is a weighted average: the average of each score over all its ratings, times that score's weight, added together. The weights sum to 1, so the rating stays between 1 and 5. A change applies to every workflow at once, including ratings given before it.",
    formula: (results: string, cost: string) => `Overall = ${results} × average results + ${cost} × average cost`,
    example: (overall: string) => `Results averaging 4 and cost averaging 5 would rate ${overall}.`,
    results: "Results weight",
    resultsHint: "How good the results were",
    cost: "Cost weight",
    costHint: "Whether it was worth what it cost",
    sum: (sum: string) => `Sum: ${sum}`,
    mustSum: (sum: string) => `The weights must sum to 1; they sum to ${sum}.`,
    badWeight: "Enter a number from 0 to 1.",
    save: "Save weights",
    reset: "Reset to default (0.6 / 0.4)",
    saved: "Rating weights saved",
    defaults: "Default weights",
    setBy: (who: string, when: string) => `Set by ${who} ${when}`,
    readOnly: "Changing the weights needs the ManageRatingWeights permission.",
    loadError: "Could not load the rating weights",
  },
  rate: {
    title: "Rate this workflow",
    ownersRating: "The owner's rating",
    reasons: {
      not_finished: "You can change it once the task finishes.",
      never_ran: "The task stopped before its first step, so there is nothing of the workflow to rate.",
      not_owner: "Only the task group's owner can rate this task.",
    } satisfies Record<NotRateableReason, string>,
    yourRating: "Your rating",
    description: "How the workflow did on this task. Your scores count toward its overall rating.",
    resultsQuestion: "How good were the results?",
    costQuestion: "Was it worth what it cost?",
    taskCost: (amount: string) => `This task cost ${amount}`,
    score: (n: number, label: string) => `${n} of 5, ${label}`,
    review: "Review (optional)",
    reviewPlaceholder: "What went well, and what did not",
    counter: (n: string, max: string) => `${n} / ${max}`,
    submit: "Submit rating",
    update: "Save changes",
    edit: "Edit rating",
    saved: "Thanks, your rating is saved",
    updated: "Rating updated",
    hiddenNote: "Your review was hidden by a moderator; its scores still count.",
    chooseBoth: "Choose a score for results and for cost.",
    rated: (when: string) => `Rated ${when}`,
    noReview: "No written review",
    loadError: "Could not load your rating",
  },
};
